import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  MapType,
  MapPolygon,
  MapMarkersRequest,
  MapLegendItem,
} from '../../types/maps';
import { getMapTypes, getMapMarkers } from '../../endpoints/maps';
import { useApiGet } from '../../utils/httpClientTs';
import { config } from '../../utils/config';
import AlertItem from '../../Components/AlertItem';

// ISO 3166-1 alpha-2, or '__global__' to omit Mapbox `country` (wider / ambiguous results).
const MAP_GEOCODE_COUNTRIES: { value: string; label: string }[] = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'MX', label: 'Mexico' },
  { value: '__global__', label: 'All countries' },
];

// Set the access token from config
mapboxgl.accessToken =
  config.MAPBOX_ACCESS_TOKEN || 'pk.your_mapbox_token_here';

interface MapProps {}

const Map: React.FC<MapProps> = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedMapType, setSelectedMapType] = useState<string>('chapters');
  const [postalCode, setPostalCode] = useState<string>('');
  const [searchCountry, setSearchCountry] = useState<string>('US');
  const [distance, setDistance] = useState<number>(30);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchCenter, setSearchCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [legend, setLegend] = useState<MapLegendItem[]>([]);
  const markersDataRef = useRef<any[]>([]);
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  // Get map types
  const { data: mapTypes, isLoading: mapTypesLoading } = useApiGet<MapType[]>(
    ['map-types'],
    getMapTypes,
    { enabled: true }
  );

  // Update markers using Mapbox native source/layer approach
  const updateMarkers = useCallback(() => {
    if (!map.current || !mapLoaded || !markersDataRef.current.length) return;

    // Check if map style is loaded before adding markers
    if (!map.current.isStyleLoaded()) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Group markers by coordinates for clustering
    const coordinateGroups: { [key: string]: any[] } = {};

    markersDataRef.current.forEach((marker) => {
      // Validate coordinates
      if (
        typeof marker.longitude !== 'number' ||
        typeof marker.latitude !== 'number' ||
        isNaN(marker.longitude) ||
        isNaN(marker.latitude)
      ) {
        console.warn('Invalid coordinates for marker:', marker);
        return;
      }

      // Use 3 decimal place precision for 30-mile map scale (~360 feet precision)
      const lng = Number(marker.longitude.toFixed(3));
      const lat = Number(marker.latitude.toFixed(3));
      const key = `${lng},${lat}`;

      if (!coordinateGroups[key]) {
        coordinateGroups[key] = [];
      }
      coordinateGroups[key].push(marker);
    });

    // Create GeoJSON data for Mapbox source
    const features = Object.entries(coordinateGroups).map(
      ([coords, markers]) => {
        const longitude = markers[0].longitude;
        const latitude = markers[0].latitude;

        console.log(`Creating feature for ${markers.length} markers at:`, {
          longitude,
          latitude,
          markers: markers.map((m) => ({ id: m.id, name: m.name })),
        });

        return {
          type: 'Feature' as const,
          properties: {
            id: markers[0].id,
            name: markers[0].name,
            extra_info: markers[0].extra_info,
            pin_color: markers[0].pin_color,
            marker_count: markers.length,
            marker_ids: markers.map((m) => m.id), // Store just the IDs
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [longitude, latitude],
          },
        };
      }
    );

    const geojsonData = {
      type: 'FeatureCollection' as const,
      features,
    };

    // Add or update the source
    if (map.current.getSource('markers')) {
      (map.current.getSource('markers') as mapboxgl.GeoJSONSource).setData(
        geojsonData
      );
    } else {
      map.current.addSource('markers', {
        type: 'geojson',
        data: geojsonData,
      });

      // Add the circle layer
      map.current.addLayer({
        id: 'markers',
        type: 'circle',
        source: 'markers',
        paint: {
          'circle-radius': [
            'case',
            ['>', ['get', 'marker_count'], 1],
            20, // Cluster size
            10, // Single marker size
          ],
          'circle-color': ['get', 'pin_color'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Add text layer for cluster counts
      map.current.addLayer({
        id: 'marker-labels',
        type: 'symbol',
        source: 'markers',
        layout: {
          'text-field': [
            'case',
            ['>', ['get', 'marker_count'], 1],
            ['get', 'marker_count'], // Show count for clusters
            '', // No text for single markers
          ],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-offset': [0, 0],
          'text-anchor': 'center',
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 1,
        },
      });

      // Add click handler for circles
      map.current.on('click', 'markers', (e) => {
        const feature = e.features?.[0];
        if (feature) {
          if (popupRef.current) {
            popupRef.current.remove();
          }

          const markerCount = feature.properties.marker_count;
          let popupContent;

          if (markerCount > 1) {
            // Look up full marker data using IDs
            const markerIds = feature.properties.marker_ids || [];
            const fullMarkers = markersDataRef.current.filter((marker) =>
              markerIds.includes(marker.id)
            );
            popupContent = createClusterPopupContent(fullMarkers);
          } else {
            popupContent = createPopupContent(feature.properties);
          }

          popupRef.current = new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: false,
          })
            .setLngLat(e.lngLat)
            .setHTML(popupContent)
            .addTo(map.current!);
        }
      });

      // Add click handler for text labels (same functionality)
      map.current.on('click', 'marker-labels', (e) => {
        const feature = e.features?.[0];
        if (feature) {
          if (popupRef.current) {
            popupRef.current.remove();
          }

          const markerCount = feature.properties.marker_count;
          let popupContent;

          if (markerCount > 1) {
            // Look up full marker data using IDs
            const markerIds = feature.properties.marker_ids || [];
            const fullMarkers = markersDataRef.current.filter((marker) =>
              markerIds.includes(marker.id)
            );
            popupContent = createClusterPopupContent(fullMarkers);
          } else {
            popupContent = createPopupContent(feature.properties);
          }

          popupRef.current = new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: false,
          })
            .setLngLat(e.lngLat)
            .setHTML(popupContent)
            .addTo(map.current!);
        }
      });

      // Change cursor on hover
      map.current.on('mouseenter', 'markers', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'markers', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });

      // Also handle hover for text labels
      map.current.on('mouseenter', 'marker-labels', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = 'pointer';
        }
      });

      map.current.on('mouseleave', 'marker-labels', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = '';
        }
      });
    }
  }, [mapLoaded]);

  // Debounced function to fetch markers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchMarkers = useCallback(
    debounce(async () => {
      if (!map.current || !mapLoaded) return;

      const bounds = map.current.getBounds();
      const polygon: MapPolygon = {
        min_latitude: bounds.getSouth(),
        max_latitude: bounds.getNorth(),
        min_longitude: bounds.getWest(),
        max_longitude: bounds.getEast(),
      };

      // Inline fetchMarkers logic to avoid circular dependency
      setIsLoading(true);
      setError(null);

      try {
        let bounds: mapboxgl.LngLatBounds;

        if (polygon) {
          bounds = new mapboxgl.LngLatBounds(
            [polygon.min_longitude, polygon.min_latitude],
            [polygon.max_longitude, polygon.max_latitude]
          );
        } else {
          bounds = map.current.getBounds();
        }

        const boundsArray = [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth(),
        ];

        const request: MapMarkersRequest = {
          map_type_id: selectedMapType,
          polygon: {
            min_latitude: boundsArray[1],
            max_latitude: boundsArray[3],
            min_longitude: boundsArray[0],
            max_longitude: boundsArray[2],
          },
        };

        const response = await getMapMarkers(request);

        if (response.markers) {
          // Store markers data
          markersDataRef.current = response.markers;

          // Store legend data
          if (response.legend) {
            setLegend(response.legend);
          }

          // Update markers
          updateMarkers();
        }
      } catch (err) {
        console.error('Error fetching markers:', err);
        setError('Failed to fetch map data');
      } finally {
        setIsLoading(false);
      }
    }, 1000),
    [selectedMapType, mapLoaded, updateMarkers]
  );

  // Add or update radius circle on map using GeoJSON
  const updateRadiusCircle = useCallback(
    (center: { lat: number; lng: number }, radiusMiles: number) => {
      if (!map.current || !mapLoaded) return;

      // Check if map style is loaded before adding sources/layers
      if (!map.current.isStyleLoaded()) {
        // Wait for style to load, then try again
        map.current.on('styledata', () => {
          if (map.current && map.current.isStyleLoaded()) {
            updateRadiusCircle(center, radiusMiles);
          }
        });
        return;
      }

      // Convert miles to meters (approximate)
      const radiusMeters = radiusMiles * 1609.34;

      // Create circle geometry
      const circle = createCircleGeometry(center.lng, center.lat, radiusMeters);

      // Add or update the circle source
      if (map.current.getSource('radius-circle')) {
        (
          map.current.getSource('radius-circle') as mapboxgl.GeoJSONSource
        ).setData(circle);
      } else {
        map.current.addSource('radius-circle', {
          type: 'geojson',
          data: circle,
        });

        // Add circle layer
        map.current.addLayer({
          id: 'radius-circle-fill',
          type: 'fill',
          source: 'radius-circle',
          paint: {
            'fill-color': '#1976d2',
            'fill-opacity': 0.1,
          },
        });

        map.current.addLayer({
          id: 'radius-circle-stroke',
          type: 'line',
          source: 'radius-circle',
          paint: {
            'line-color': '#1976d2',
            'line-opacity': 0.5,
            'line-width': 2,
          },
        });
      }
    },
    [mapLoaded]
  );

  // Helper function to calculate bounds from center and radius
  const calculateBoundsFromRadius = (
    center: { lat: number; lng: number },
    radiusMiles: number
  ): MapPolygon => {
    // Convert miles to meters
    const radiusMeters = radiusMiles * 1609.34;

    // Approximate conversion from meters to degrees
    // 1 degree latitude ≈ 111,320 meters
    // 1 degree longitude ≈ 111,320 * cos(latitude) meters
    const latDelta = radiusMeters / 111320;
    const lngDelta =
      radiusMeters / (111320 * Math.cos((center.lat * Math.PI) / 180));

    const bounds = {
      min_latitude: center.lat - latDelta,
      max_latitude: center.lat + latDelta,
      min_longitude: center.lng - lngDelta,
      max_longitude: center.lng + lngDelta,
    };

    return bounds;
  };

  // Helper function to create popup content
  const createClusterPopupContent = (markers: any[]) => {
    // Ensure markers is an array - handle both array and JSON string cases
    let markersArray = [];
    if (Array.isArray(markers)) {
      markersArray = markers;
    } else if (typeof markers === 'string') {
      try {
        markersArray = JSON.parse(markers);
      } catch (e) {
        console.error('Failed to parse markers JSON:', e);
        markersArray = [];
      }
    }

    const maxDisplay = 10;
    const displayMarkers = markersArray.slice(0, maxDisplay);
    const remainingCount = Math.max(0, markersArray.length - maxDisplay);

    const recordsList = displayMarkers
      .map((marker) => {
        const extraInfo = marker.extra_info
          ? `<div style="font-size: 11px; color: #666; margin-top: 2px;">
              ${marker.extra_info}
            </div>`
          : '';

        return `
          <div style="margin-bottom: 8px; padding: 4px; background: #f5f5f5; border-radius: 4px;">
            <div style="font-weight: bold; color: #1976d2; font-size: 13px;">
              ${marker.name}
            </div>
            ${extraInfo}
          </div>
        `;
      })
      .join('');

    const moreText =
      remainingCount > 0
        ? `<div style="margin-top: 8px; font-style: italic; color: #666; text-align: center;">
            ... and ${remainingCount} more
          </div>`
        : '';

    return `
      <div style="padding: 8px; font-family: Arial, sans-serif; max-width: 300px; max-height: 400px; overflow-y: auto;">
        <div style="font-weight: bold; color: #1976d2; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px;">
          ${markersArray.length} markers
        </div>
        ${recordsList}
        ${moreText}
      </div>
    `;
  };

  const createPopupContent = (marker: any) => {
    // This function is now only used for individual markers
    // Clusters use createClusterPopupContent instead
    const extraInfo = marker.extra_info
      ? `<div style="margin-top: 4px; font-size: 12px; color: #666;">
        ${marker.extra_info}
      </div>`
      : '';

    return `
      <div style="padding: 8px; font-family: Arial, sans-serif; max-width: 200px;">
        <div style="font-weight: bold; color: #1976d2;">
          ${marker.name}
        </div>
        ${extraInfo}
      </div>
    `;
  };

  // Helper function to create circle geometry
  const createCircleGeometry = (
    lng: number,
    lat: number,
    radiusMeters: number
  ) => {
    const points = 64; // Number of points to create a smooth circle
    const coordinates = [];

    for (let i = 0; i < points; i++) {
      const angle = (i * 360) / points;
      const dx = radiusMeters * Math.cos((angle * Math.PI) / 180);
      const dy = radiusMeters * Math.sin((angle * Math.PI) / 180);

      // Convert meters to degrees (approximate)
      const newLng = lng + dx / (111320 * Math.cos((lat * Math.PI) / 180));
      const newLat = lat + dy / 110540;

      coordinates.push([newLng, newLat]);
    }

    // Close the polygon
    coordinates.push(coordinates[0]);

    return {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'Polygon' as const,
        coordinates: [coordinates],
      },
    };
  };

  // Get user's location for better initial center
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          if (map.current) {
            // Set search center for radius circle
            setSearchCenter({ lat: latitude, lng: longitude });

            map.current.flyTo({
              center: [longitude, latitude],
              zoom: 10,
              duration: 2000,
            });
            // Fetch markers for the user's location and show radius circle
            setTimeout(() => {
              // Calculate bounds based on user location and radius, not map view
              const searchBounds = calculateBoundsFromRadius(
                { lat: latitude, lng: longitude },
                distance
              );
              fetchMarkers(searchBounds);
              updateRadiusCircle({ lat: latitude, lng: longitude }, distance);
            }, 2500);
          }
        },
        (error) => {
          // Continue with default center
        }
      );
    }
  };

  // Fetch markers function
  const fetchMarkers = useCallback(
    async (polygon?: MapPolygon) => {
      if (!map.current || !mapLoaded) return;

      // Check if map style is loaded before proceeding
      if (!map.current.isStyleLoaded()) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        let bounds: mapboxgl.LngLatBounds;

        if (polygon) {
          bounds = new mapboxgl.LngLatBounds(
            [polygon.min_longitude, polygon.min_latitude],
            [polygon.max_longitude, polygon.max_latitude]
          );
        } else {
          bounds = map.current.getBounds();
        }

        const boundsArray = [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth(),
        ];

        const request: MapMarkersRequest = {
          map_type_id: selectedMapType,
          polygon: {
            min_latitude: boundsArray[1],
            max_latitude: boundsArray[3],
            min_longitude: boundsArray[0],
            max_longitude: boundsArray[2],
          },
        };

        const response = await getMapMarkers(request);

        if (response.markers) {
          // Store markers data
          markersDataRef.current = response.markers;

          // Store legend data
          if (response.legend) {
            setLegend(response.legend);
          }

          // Update markers
          updateMarkers();
        }
      } catch (err) {
        console.error('Error fetching markers:', err);
        setError('Failed to fetch map data');
      } finally {
        setIsLoading(false);
      }
    },
    [mapLoaded, selectedMapType, updateMarkers]
  );

  // Initialize map
  useEffect(() => {
    if (mapContainer.current && !map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-98.5795, 39.8283], // Center of US
        zoom: 4,
      });

      map.current.on('load', () => {
        setMapLoaded(true);
        // Try to get user's location for better initial experience
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              if (map.current) {
                // Set search center for radius circle
                setSearchCenter({ lat: latitude, lng: longitude });

                map.current.flyTo({
                  center: [longitude, latitude],
                  zoom: 10,
                  duration: 2000,
                });
                // Fetch markers for the user's location and show radius circle
                setTimeout(() => {
                  // Calculate bounds based on user location and radius, not map view
                  const searchBounds = calculateBoundsFromRadius(
                    { lat: latitude, lng: longitude },
                    distance
                  );
                  fetchMarkers(searchBounds);
                  updateRadiusCircle(
                    { lat: latitude, lng: longitude },
                    distance
                  );
                }, 2500);
              }
            },
            (error) => {
              // Continue with default center
            }
          );
        }
      });

      map.current.on('moveend', () => {
        // Update markers after move ends
        if (mapLoaded && markersDataRef.current.length) {
          updateMarkers();
        }
      });

      map.current.on('zoomend', () => {
        // Update markers after zoom ends
        if (mapLoaded && markersDataRef.current.length) {
          updateMarkers();
        }
      });
    }

    return () => {
      if (map.current) {
        // Clean up radius circle layers
        if (map.current.getLayer('radius-circle-fill')) {
          map.current.removeLayer('radius-circle-fill');
        }
        if (map.current.getLayer('radius-circle-stroke')) {
          map.current.removeLayer('radius-circle-stroke');
        }
        if (map.current.getSource('radius-circle')) {
          map.current.removeSource('radius-circle');
        }

        // Clean up markers layers
        if (map.current.getLayer('marker-labels')) {
          map.current.removeLayer('marker-labels');
        }
        if (map.current.getLayer('markers')) {
          map.current.removeLayer('markers');
        }
        if (map.current.getSource('markers')) {
          map.current.removeSource('markers');
        }

        // Clean up popup
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        map.current.remove();
        map.current = null;
      }
    };
  }, [
    mapLoaded,
    debouncedFetchMarkers,
    distance,
    fetchMarkers,
    updateRadiusCircle,
    updateMarkers,
  ]);

  // Enhanced location search using Mapbox Geocoding API
  const handleLocationSearch = async () => {
    if (!postalCode.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const searchParams = new URLSearchParams({
        access_token: mapboxgl.accessToken || '',
        limit: '1',
      });
      if (searchCountry && searchCountry !== '__global__') {
        searchParams.set('country', searchCountry);
      }
      const geocodePath = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(postalCode.trim())}.json?${searchParams.toString()}`;
      const response = await fetch(geocodePath);
      const data = await response.json();

      if (!response.ok) {
        const msg = data?.message || response.statusText || 'Geocoding failed';
        throw new Error(msg);
      }
      if (!data.features?.length) {
        throw new Error(
          'No results for this place. Try a different address or change the search country above.'
        );
      }
      const [lng, lat] = data.features[0].center;
      const coordinates = { lat, lng };

      if (map.current) {
        // Set search center for radius circle
        setSearchCenter({ lat: coordinates.lat, lng: coordinates.lng });

        map.current.flyTo({
          center: [coordinates.lng, coordinates.lat],
          zoom: 10,
          duration: 2000,
        });

        // Wait for the map to finish moving, then fetch markers and show radius circle
        setTimeout(() => {
          // Calculate bounds based on search center and radius, not map view
          const searchBounds = calculateBoundsFromRadius(
            { lat: coordinates.lat, lng: coordinates.lng },
            distance
          );
          fetchMarkers(searchBounds);
          updateRadiusCircle(
            { lat: coordinates.lat, lng: coordinates.lng },
            distance
          );
        }, 2500);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to find location. Try a postal code, city name, or address, or adjust the search country.';
      setError(message);
      console.error('Error geocoding location:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle map type change
  const handleMapTypeChange = (newMapType: string) => {
    setSelectedMapType(newMapType);

    // Clear existing markers and data when map type changes
    markersDataRef.current = [];
    setLegend([]);

    // Clear markers from map
    if (map.current && map.current.getSource('markers')) {
      (map.current.getSource('markers') as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [],
      });
    }

    // Don't automatically fetch markers - wait for user to search
  };

  // Handle manual refresh
  const handleManualRefresh = () => {
    fetchMarkers();
  };

  // Handle distance change - update radius circle and refetch markers if there's an active search
  const handleDistanceChange = (newDistance: number) => {
    setDistance(newDistance);
    if (searchCenter) {
      updateRadiusCircle(searchCenter, newDistance);
      // Recalculate bounds and fetch markers with new radius
      const searchBounds = calculateBoundsFromRadius(searchCenter, newDistance);
      fetchMarkers(searchBounds);
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Controls Panel */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 'bold', color: 'primary.main' }}
        >
          P.E.O. Map
        </Typography>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Map Type</InputLabel>
          <Select
            value={selectedMapType}
            label="Map Type"
            onChange={(e) => handleMapTypeChange(e.target.value)}
            disabled={mapTypesLoading}
          >
            {mapTypes?.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {type.id === 'chapters' ? (
                    <GroupIcon fontSize="small" />
                  ) : (
                    <PersonIcon fontSize="small" />
                  )}
                  {type.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Search country</InputLabel>
          <Select
            value={searchCountry}
            label="Search country"
            onChange={(e) => setSearchCountry(e.target.value)}
          >
            {MAP_GEOCODE_COUNTRIES.map((c) => (
              <MenuItem key={c.value || 'global'} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Location (postal, city, address)"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          sx={{ width: 200 }}
          placeholder="e.g. 90210, Toronto, 123 Main St"
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Search Radius</InputLabel>
          <Select
            value={distance}
            label="Search Radius"
            onChange={(e) => handleDistanceChange(Number(e.target.value))}
          >
            <MenuItem value={15}>15 miles</MenuItem>
            <MenuItem value={30}>30 miles</MenuItem>
            <MenuItem value={50}>50 miles</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<LocationIcon />}
          onClick={handleLocationSearch}
          disabled={isLoading || !postalCode.trim()}
          sx={{ backgroundColor: '#1976d2' }} // Blue color for edit elements per memory
        >
          Search
        </Button>

        <Button
          variant="outlined"
          startIcon={<LocationIcon />}
          onClick={getUserLocation}
          disabled={isLoading}
          sx={{ borderColor: '#1976d2', color: '#1976d2' }} // Blue color for edit elements per memory
        >
          Use My Location
        </Button>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleManualRefresh}
          disabled={isLoading}
          sx={{ borderColor: '#1976d2', color: '#1976d2' }} // Blue color for edit elements per memory
        >
          Refresh
        </Button>

        {isLoading && <CircularProgress size={20} />}
      </Paper>

      {/* Map Container */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <div
          ref={mapContainer}
          style={{
            height: '100%',
            width: '100%',
          }}
        />

        {/* Map Info Overlay */}
        <Card
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            minWidth: 200,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" gutterBottom>
              Legend
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {legend.length > 0 ? (
                legend
                  .sort((a, b) => a.order - b.order)
                  .map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        }}
                      />
                      <Typography variant="body2">{item.label}</Typography>
                    </Box>
                  ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No legend data available
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            <AlertItem
              severity="error"
              description={error}
              onClose={() => setError(null)}
              keepOpen={true}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default Map;
